import {
  BrokerageDates,
  RateQuote,
  RateSelectionReason,
  RateShop,
  ShipmentSpec,
  ShipmentSpecInviteCarriersToSpotMarketCommand,
  SpotMarket,
  TenderToCarrierRequest,
  WaterfallTender,
  WaterfallTenderConfig,
} from '@/model';
import { AcceptRateQuoteRequest } from '@/model/rate/AcceptRateQuoteRequest';

import { BaseHttpController } from './BaseHttpController';

const endpoint = 'shipment-spec';

export class ShipmentSpecController extends BaseHttpController {
  constructor() {
    super(endpoint);
  }

  async loadRates(shipmentSpecId: number): Promise<RateShop> {
    const response = await this.http.get<RateShop>(`${shipmentSpecId}/rate`);
    return new RateShop(response.data);
  }

  async findSpotMarketsByRateShopId(
    shipmentSpecId: number,
    rateShopId: number,
  ): Promise<SpotMarket[]> {
    const response = await this.http.get<SpotMarket[]>(
      `${shipmentSpecId}/rate-shop/${rateShopId}/spot-market`,
    );
    return response.data.map((s) => new SpotMarket(s));
  }

  async generateRates(shipmentSpecId: number): Promise<RateShop> {
    const response = await this.http.post<RateShop>(
      `${shipmentSpecId}/rate/generate`,
    );
    return new RateShop(response.data);
  }

  async acceptRateQuote(
    shipmentSpecId: number,
    acceptRateQuoteRequest: AcceptRateQuoteRequest,
  ): Promise<{ shipmentId: number }> {
    const response = await this.http.post<{ shipmentId: number }>(
      `${shipmentSpecId}/rate/${acceptRateQuoteRequest.rateQuoteId}/accept`,
      acceptRateQuoteRequest,
    );
    return response.data;
  }

  async acceptRateQuoteAndTender(
    shipmentSpecId: number,
    rateQuoteId: number,
    tenderToCarrierRequest: TenderToCarrierRequest,
  ): Promise<{ shipmentId: number }> {
    const response = await this.http.post<{ shipmentId: number }>(
      `${shipmentSpecId}/rate/${rateQuoteId}/accept-and-tender`,
      tenderToCarrierRequest,
    );
    return response.data;
  }

  async initiateWaterfallTender(
    shipmentSpecId: number,
    waterfallTenderConfig: WaterfallTenderConfig,
  ): Promise<WaterfallTender> {
    const response = await this.http.post<WaterfallTender>(
      `${shipmentSpecId}/rate/waterfall-tender`,
      waterfallTenderConfig,
    );
    return new WaterfallTender(response.data);
  }

  async addSpotQuote(
    shipmentSpec: ShipmentSpec,
    spotQuote: RateQuote,
  ): Promise<RateQuote> {
    if (!shipmentSpec.id) {
      throw new Error('shipmentSpec id is required to create spot quote');
    }
    const response = await this.http.post<RateQuote>(
      `${shipmentSpec.id}/rate/spot-quote`,
      spotQuote,
    );
    return new RateQuote(response.data);
  }

  async addLaneQuote(
    shipmentSpecId: number,
    laneQuote: RateQuote,
  ): Promise<RateQuote> {
    if (!shipmentSpecId) {
      throw new Error('shipmentSpec id is required to create spot quote');
    }
    const response = await this.http.post<RateQuote>(
      `${shipmentSpecId}/rate/lane-quote`,
      laneQuote,
    );
    return new RateQuote(response.data);
  }

  async addBrokerage(
    shipmentSpecId: number,
    brokerageQuote: RateQuote,
    brokerageDates?: BrokerageDates,
    rateSelectionReason?: RateSelectionReason,
  ): Promise<{ shipmentId: number }> {
    if (!shipmentSpecId) {
      throw new Error('shipmentSpec id is required to create brokerage quote');
    }

    const response = await this.http.post<{ shipmentId: number }>(
      `${shipmentSpecId}/rate/broker`,
      {
        shipmentSpecId,
        brokerageQuoteDto: brokerageQuote,
        brokerageDatesDto: brokerageDates,
        rateSelectionReason,
      },
    );
    return response.data;
  }

  async updateRateQuote(
    shipmentSpec: ShipmentSpec,
    rateQuote: RateQuote,
  ): Promise<RateQuote> {
    if (!shipmentSpec.id) {
      throw new Error('shipmentSpec id is required to update spot quote');
    }
    if (!rateQuote.id) {
      throw new Error('rateQuote id is required to update rate quote');
    }
    const response = await this.http.put<RateQuote>(
      `${shipmentSpec.id}/rate/${rateQuote.id}`,
      rateQuote,
    );
    return new RateQuote(response.data);
  }

  async deleteSpotQuote(
    shipmentSpec: ShipmentSpec,
    spotQuote: RateQuote,
  ): Promise<void> {
    if (!shipmentSpec.id) {
      throw new Error('shipmentSpec id is required to delete spot quote');
    }
    if (!spotQuote.id) {
      throw new Error('spotQuote id is required to delete spot quote');
    }
    await this.http.delete<void>(
      `${shipmentSpec.id}/rate/spot-quote/${spotQuote.id}`,
    );
  }

  async loadSpotMarket(
    shipmentSpecId: number,
    carrierId: number,
  ): Promise<SpotMarket> {
    const response = await this.http.get<SpotMarket>(
      `${shipmentSpecId}/spot-market`,
      { params: { carrierId } },
    );

    return new SpotMarket(response.data);
  }

  async rejectSpotMarket(
    shipmentSpecId: number,
    spotMarketId: number,
  ): Promise<void> {
    const response = await this.http.post<void>(
      `${shipmentSpecId}/spot-market/${spotMarketId}/reject`,
    );
    return response.data;
  }

  async cancelSpotMarket(
    shipmentSpecId: number,
    spotMarketId: number,
  ): Promise<void> {
    await this.http.post<void>(
      `${shipmentSpecId}/spot-market/${spotMarketId}/cancel`,
    );
  }

  async resendSpotMarket(
    shipmentSpecId: number,
    spotMarketId: number,
  ): Promise<void> {
    await this.http.post<void>(
      `${shipmentSpecId}/spot-market/${spotMarketId}/resend`,
    );
  }

  async logFirstSpotMarketVisit(
    shipmentSpecId: number,
    spotMarketId: number,
  ): Promise<void> {
    await this.http.post<void>(
      `${shipmentSpecId}/spot-market/${spotMarketId}/first-visit`,
    );
  }

  async inviteCarriersToSpotMarket(
    shipmentSpecId: number,
    command: ShipmentSpecInviteCarriersToSpotMarketCommand,
  ): Promise<SpotMarket[]> {
    const response = await this.http.post<SpotMarket[]>(
      `${shipmentSpecId}/spot-market/invite`,
      command,
    );
    return response.data.map((s) => new SpotMarket(s));
  }

  async addSpotMarketBid(
    shipmentSpecId: number,
    spotMarketId: number,
    spotMarketBid: RateQuote,
  ): Promise<RateQuote> {
    const response = await this.http.post<RateQuote>(
      `${shipmentSpecId}/spot-market/${spotMarketId}/bid`,
      spotMarketBid,
    );
    return new RateQuote(response.data);
  }

  async deleteSpotMarketBid(
    shipmentSpecId: number,
    spotMarketId: number,
    bidId: number,
  ): Promise<void> {
    await this.http.delete<void>(
      `${shipmentSpecId}/spot-market/${spotMarketId}/bid/${bidId}`,
    );
  }
}
